/**
 * Afriqahome Leads API
 * P1.2: Real leads/contact requests to database
 */

import { apiGet, apiPost, apiPatch, SUPABASE_URL, SUPABASE_ANON_KEY } from './client';
import { getValidAccessToken } from './authApi';

export interface DbLead {
  id: string;
  listing_id: string;
  agent_id: string;
  buyer_id: string | null;
  buyer_name: string | null;
  buyer_email: string | null;
  buyer_phone: string | null;
  message: string | null;
  status: 'new' | 'contacted' | 'closed';
  created_at: string;
}

export interface CreateLeadParams {
  listingId: string;
  agentId: string;
  buyerId?: string;
  buyerName?: string;
  buyerEmail?: string;
  buyerPhone?: string;
  message?: string;
}

/**
 * Create a new lead (contact request)
 */
export async function createLead(params: CreateLeadParams): Promise<{
  success: boolean;
  leadId?: string;
  error?: string
}> {
  const accessToken = await getValidAccessToken();
  
  const leadData = {
    listing_id: params.listingId,
    agent_id: params.agentId,
    buyer_id: params.buyerId || null,
    buyer_name: params.buyerName || null,
    buyer_email: params.buyerEmail || null,
    buyer_phone: params.buyerPhone || null,
    message: params.message || null,
    status: 'new'
  };

  const { data, error } = await apiPost<DbLead[]>(
    'leads',
    leadData,
    accessToken || undefined
  );

  if (error || !data || data.length === 0) {
    return { success: false, error: error?.message || 'Failed to send message' };
  }

  // Send email notification to agent (fire-and-forget, don't block on failure)
  try {
    await sendLeadNotificationEmail(data[0].id);
  } catch (emailError) {
    // Don't fail the lead creation if email fails
  }

  return { success: true, leadId: data[0].id };
}

/**
 * Send email notification to agent when new lead is created
 * This is fire-and-forget - failures don't affect lead creation
 */
async function sendLeadNotificationEmail(leadId: string): Promise<void> {
  const accessToken = await getValidAccessToken();

  // SEC-01 (after TECH-016): send the lead id and nothing else. The
  // send-lead-notification Edge Function reads the lead, resolves the
  // listing's agent and the address server-side (service role), and notifies
  // each lead at most once, within 10 minutes of its creation.
  const response = await fetch(
    `${SUPABASE_URL}/functions/v1/send-lead-notification`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken || SUPABASE_ANON_KEY}`,
        'apikey': SUPABASE_ANON_KEY
      },
      body: JSON.stringify({ lead_id: leadId })
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to send notification');
  }
}

/**
 * Get leads for an agent (their listings)
 * NOTE: This is tier-protected - only Elevate/Dominate users can access CRM
 */
export async function getAgentLeads(agentId: string): Promise<DbLead[]> {
  const accessToken = await getValidAccessToken();

  if (!accessToken) {
    return [];
  }

  try {
    // Use secure Edge Function with tier validation
    const response = await fetch(
      `${SUPABASE_URL}/functions/v1/leads-service`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'apikey': SUPABASE_ANON_KEY
        },
        body: JSON.stringify({ action: 'get_leads' })
      }
    );

    const result = await response.json();

    if (!response.ok) {
      return [];
    }

    return result.leads || [];
  } catch (error) {
    return [];
  }
}

/**
 * Teaser data for leads (available to ALL tiers)
 * Used to show Ignite users they have leads waiting
 */
export interface LeadsTeaserData {
  totalCount: number;
  newCount: number;
  hasCRMAccess: boolean;
  currentTier: string;
}

/**
 * Get leads teaser data (count only, no details)
 * Available to ALL tiers - used to show Ignite users they have leads waiting
 */
export async function getLeadsTeaser(): Promise<LeadsTeaserData> {
  const accessToken = await getValidAccessToken();

  if (!accessToken) {
    return { totalCount: 0, newCount: 0, hasCRMAccess: false, currentTier: 'ignite' };
  }

  try {
    const response = await fetch(
      `${SUPABASE_URL}/functions/v1/leads-service`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'apikey': SUPABASE_ANON_KEY
        },
        body: JSON.stringify({ action: 'get_lead_count_teaser' })
      }
    );

    const result = await response.json();

    if (!response.ok) {
      return { totalCount: 0, newCount: 0, hasCRMAccess: false, currentTier: 'ignite' };
    }

    return {
      totalCount: result.totalCount || 0,
      newCount: result.newCount || 0,
      hasCRMAccess: result.hasCRMAccess ?? false,
      currentTier: result.currentTier || 'ignite'
    };
  } catch (error) {
    return { totalCount: 0, newCount: 0, hasCRMAccess: false, currentTier: 'ignite' };
  }
}

/**
 * Get unread leads count for an agent
 * NOTE: Returns 0 for Ignite users (no CRM access)
 */
export async function getUnreadLeadsCount(agentId: string): Promise<number> {
  const accessToken = await getValidAccessToken();

  if (!accessToken) {
    return 0;
  }

  try {
    // Use secure Edge Function with tier validation
    const response = await fetch(
      `${SUPABASE_URL}/functions/v1/leads-service`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'apikey': SUPABASE_ANON_KEY
        },
        body: JSON.stringify({ action: 'get_unread_count' })
      }
    );

    const result = await response.json();

    if (!response.ok) {
      return 0;
    }

    return result.count || 0;
  } catch (error) {
    return 0;
  }
}

/**
 * Update lead status
 * NOTE: This is tier-protected - only Elevate/Dominate users can update leads
 */
export async function updateLeadStatus(
  leadId: string,
  status: 'new' | 'contacted' | 'closed'
): Promise<{ success: boolean; error?: string }> {
  const accessToken = await getValidAccessToken();

  if (!accessToken) {
    return { success: false, error: 'Not authenticated' };
  }

  try {
    // Use secure Edge Function with tier validation
    const response = await fetch(
      `${SUPABASE_URL}/functions/v1/leads-service`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'apikey': SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          action: 'update_lead_status',
          leadId,
          status
        })
      }
    );

    const result = await response.json();

    if (!response.ok) {
      if (result.code === 'TIER_REQUIRED') {
        return { success: false, error: 'CRM access requires Elevate or higher tier' };
      }
      return { success: false, error: result.error || 'Failed to update lead status' };
    }

    return { success: true };
  } catch (error: unknown) {
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

/**
 * Get leads for a specific listing
 */
export async function getListingLeads(listingId: string): Promise<DbLead[]> {
  const accessToken = await getValidAccessToken();

  if (!accessToken) {
    return [];
  }

  const { data, error } = await apiGet<DbLead[]>(
    'leads',
    `listing_id=eq.${listingId}&order=created_at.desc&select=*`,
    accessToken
  );

  if (error || !data) {
    return [];
  }

  return data;
}

/**
 * Extended lead type with property and agent details for buyer inquiries view
 */
export interface BuyerInquiry extends DbLead {
  property?: {
    id: string;
    title: string;
    city: string;
    image_url: string | null;
  };
  agent?: {
    id: string;
    full_name: string;
    agency_name?: string;
  };
}

/**
 * Get inquiries/messages sent by a buyer
 * This fetches leads where the buyer_id matches the current user
 */
export async function getBuyerInquiries(buyerId: string): Promise<BuyerInquiry[]> {
  const accessToken = await getValidAccessToken();

  if (!accessToken) {
    return [];
  }

  try {
    // Fetch leads where buyer_id matches
    const { data: leadsData, error: leadsError } = await apiGet<DbLead[]>(
      'leads',
      `buyer_id=eq.${buyerId}&order=created_at.desc&select=*`,
      accessToken
    );

    if (leadsError || !leadsData) {
      return [];
    }

    // If no leads, return empty
    if (leadsData.length === 0) {
      return [];
    }

    // Get unique listing IDs and agent IDs
    const listingIds = [...new Set(leadsData.map(l => l.listing_id))];
    const agentIds = [...new Set(leadsData.map(l => l.agent_id))];

    // Fetch listings with correct field names (title, address, city, image_url)
    const { data: listingsData } = await apiGet<Array<{
      id: string;
      title: string;
      address: string | null;
      city: string;
      image_url: string | null;
    }>>(
      'listings',
      `id=in.(${listingIds.join(',')})&select=id,title,address,city,image_url`,
      accessToken
    );

    // Fetch agents from profiles (agency_name, not company_name)
    const { data: agentsData } = await apiGet<Array<{
      id: string;
      full_name: string | null;
      agency_name: string | null;
    }>>(
      'profiles',
      `id=in.(${agentIds.join(',')})&select=id,full_name,agency_name`,
      accessToken
    );

    // Map listings and agents to leads
    const listingsMap = new Map(listingsData?.map(l => [l.id, l]) || []);
    const agentsMap = new Map(agentsData?.map(a => [a.id, a]) || []);

    return leadsData.map(lead => {
      const listing = listingsMap.get(lead.listing_id);
      const agent = agentsMap.get(lead.agent_id);

      return {
        ...lead,
        property: listing ? {
          id: listing.id,
          // Use address as title if title is empty, following the pattern in dbListingToProperty
          title: listing.address || listing.title || 'Property',
          city: listing.city,
          image_url: listing.image_url
        } : undefined,
        agent: agent ? {
          id: agent.id,
          full_name: agent.full_name || 'Agent',
          agency_name: agent.agency_name || undefined
        } : undefined
      };
    });
  } catch (error) {
    console.error('Failed to fetch buyer inquiries:', error);
    return [];
  }
}